import { DatePipe } from '@angular/common';
import { Component, signal, computed, inject, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { HeaderComponent } from '../../components/header/header.component';
import { Router } from '@angular/router';
import { AppointmentService } from '../../services/appointment.service';
import { Appointment } from '../../models/appointment';
import { AUTH_SERVICE, AuthService } from '../../services/auth.service';
import { PsychologistService } from '../../services/psychologist.service';
import { Psychologist } from '../../models/psychologists';
import TYPES from '../../constants/types';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EdgeFunctionCallerService } from '../../services/edge-function-caller.service';
import { MatIcon } from '@angular/material/icon';
import { Holiday } from '../../models/holiday';
import { GoogleAccountService } from '../../services/google-account.service';
import { GoogleAccount } from '../../models/google-account';
import { getLocalDateIso, getLocalDateKey } from '../../util/appointment-date.util';
@Component({
  selector: 'app-calendar',
  imports: [
    DatePipe,
    HeaderComponent,
    TranslatePipe,
    MatIcon
  ],
  templateUrl: './calendar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./calendar.component.scss'],
})
export class CalendarComponent {
  authService: AuthService = inject(AUTH_SERVICE);
  psychologistService: PsychologistService<Psychologist> = inject(PsychologistService<Psychologist>);
  appointmentService: AppointmentService<Appointment> = inject(AppointmentService<Appointment>);
  edgeFunctionCallerService: EdgeFunctionCallerService = inject(EdgeFunctionCallerService);
  googleAccountService: GoogleAccountService<GoogleAccount> = inject(GoogleAccountService<GoogleAccount>);
  translate: TranslateService = inject(TranslateService);

  appointments: WritableSignal<Map<string, Appointment[]>> = signal<Map<string, Appointment[]>>(new Map());

  loadingCalendarSync: WritableSignal<Boolean> = signal<Boolean>(false);
  calendarSynched: WritableSignal<Boolean> = signal<Boolean>(false);

  view = signal<'month'>('month');
  currentDate = signal(new Date());
  translatedDate = computed(() => {
    return this.getDateTranslated(this.currentDate());
  });

  monthDays = computed(() => {
    const base = new Date(this.currentDate());
    const year = base.getFullYear();
    const month = base.getMonth();

    const first = new Date(year, month, 1);
    const start = new Date(first);
    start.setDate(start.getDate() - ((first.getDay() + 6) % 7)); // starts on Monday

    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push(d);
    }
    return days;
  });

  holidays: Holiday[] = TYPES.APPLICATION.HOLIDAYS;

  PROFILES:any;
  STATUS:any;

  constructor(private router: Router){
    this.PROFILES = TYPES.DATABASE.PROFILES;
    this.STATUS = TYPES.DATABASE.STATUS;

    this.checkGoogleCalendarSync();

    this.loadAppoitments();
  }

  async loadAppoitments(){

    if(!this.authService.user()){
      await this.authService.getUser();
    }

    if(this.authService.user()?.profile === TYPES.DATABASE.PROFILES.PSYCHOLOGIST.key){      
      let psychologistId = this.psychologistService.psychologist()?.id;
      
      if(!psychologistId){
        const userId = this.authService.user()?.id;
        if(userId){
          await this.psychologistService.getPsychologistByUserId(userId);
          psychologistId = this.psychologistService.psychologist()?.id;
        }
      }

      if(psychologistId){
        this.appointmentService.getAllAppointmentByPsychologistId(psychologistId).then(
          (data) => { 
            let aux: Map<string, Appointment[]> = new Map(); 
            for(let appointment of data){
              if(appointment.date){
                const key = getLocalDateKey(appointment.date);
                if(aux.has(key)){
                  aux.get(key)?.push(appointment);
                }else{
                  aux.set(key, [appointment]);
                }
              }
            }
            for(let item of aux.entries()){
              aux.set(
                item[0],
                item[1].sort((a,b) => {
                  const dateA = new Date(a.date ?? '');
                  const dateB = new Date(b.date ?? '');
                  return dateA.getTime() - dateB.getTime();
                })
              );
            }
            this.appointments.set(aux);
          }
        );
      }
    }else{
      const userId = this.authService.user()?.id;
      if(userId){
        this.appointmentService.getAllAppointmentByUserId(userId).then(
          (data) => { 
            let aux: Map<string, Appointment[]> = new Map(); 
            for(let appointment of data){
              if(appointment.date){
                const key = getLocalDateKey(appointment.date);
                if(aux.has(key)){
                  aux.get(key)?.push(appointment);
                }else{
                  aux.set(key, [appointment]);
                }
              }
            }
            for(let item of aux.entries()){
              aux.set(
                item[0],
                item[1].sort((a,b) => {
                  const dateA = new Date(a.date ?? '');
                  const dateB = new Date(b.date ?? '');
                  return dateA.getTime() - dateB.getTime();
                })
              );
            }
            this.appointments.set(aux);
          }
        );
      }
    }

  }

  prev() {
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }

    const d = new Date(this.currentDate());
    if (this.view() === 'month') d.setMonth(d.getMonth() - 1);
    this.currentDate.set(d);
  }

  next() {
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }
    
    const d = new Date(this.currentDate());
    if (this.view() === 'month') d.setMonth(d.getMonth() + 1);
    this.currentDate.set(d);
  }

  openDay(appointments: Appointment[] | undefined) {
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }

    if(appointments){
      this.router.navigate(['/view-day'], {
        state: { 
          appointments: appointments, 
          date: new Date(appointments[0].date ?? '').getDate().toString().padStart(2, '0') 
            + '/'
            + (new Date(appointments[0].date ?? '').getMonth()+1).toString().padStart(2, '0')
            + '/'
            + new Date(appointments[0].date ?? '').getFullYear()
        }
      });
    }
  }

  addAppointment() {
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }

    this.router.navigate(['/add-appointment']);
  }

  goToday() {
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }

    this.currentDate.set(new Date());
  }

  isHoliday(date: Date) {
    const f = this.holidays.find(h => h.date === getLocalDateIso(date));
    return f ? f.name : null;
  }

  isWeekend(date: Date) {
    const day = date.getDay();
    return day === 0 || day === 6;
  }

  isToday(d: Date) {
    const now = new Date();
    return (
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    );
  }

  back(){
    history.back();
  }

  getDateTranslated(date: Date): string {
    return this.translate.instant('months.'+(date.getMonth()+1).toString()) + ' - ' + date.getFullYear();
  }

  getLocalDateKey(date: Date): string {
    return getLocalDateKey(date);
  }

  async linkGoogleCalendar(){
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }

    this.loadingCalendarSync.set(true);
    const userId = this.authService.user()?.id;
    if(userId){
      await this.edgeFunctionCallerService.linkGoogleCalendar(userId);
      this.loadingCalendarSync.set(false);
    }
  }

  async unlinkGoogleCalendar(){
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }
    
    this.loadingCalendarSync.set(true);
    const userId = this.authService.user()?.id;
    if(userId){
      await this.edgeFunctionCallerService.unlinkGoogleCalendar();

      this.checkGoogleCalendarSync();
    }
  }

  checkGoogleCalendarSync(){
    this.loadingCalendarSync.set(true);
    this.googleAccountService.isGoogleCalendarConnected().then(
      (connected) => {
        this.calendarSynched.set(connected);
        this.loadingCalendarSync.set(false);
      }
    );
  }
}
